import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Scale, Search, X, Trash2, ArrowLeft, Check, Eraser } from 'lucide-react';
import { useAmendMode, AmendBanner } from './useAmendMode';
import './NativeInventoryAdjustmentNew.css';

// Inventory Adjustment (Native) — quantity + GL + best-effort NetSuite in one
// posted document (cutover phase 2). Built for the closed-location write-offs:
// pick a location, add items (or "Zero out location" to load every native
// on-hand line), see the valuation (qty x cost) and GL preview (accounts +
// amounts), review, post. The server prices every line (average cost → last
// purchase price → current cost) and recomputes zero-outs at submit time.
//
// Layout follows the house template (NativePurchaseOrderNew): cards → picker →
// line editor → review → confirmation. POSTs are gated server-side behind
// NATIVE_INVENTORY_ADJUSTMENTS_ENABLED; the screen shows the gate honestly.

const money = (n) => {
  const v = Number(n) || 0;
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
};
const qty = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
};
const newRef = () => `ADJ-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

// Asset-account labels for the GL preview (NS internal id → chart account).
const ASSET_ACCOUNT_LABELS = {
  244: '1220 Inventory - Finished Goods (MFG)',
  126: '1210 Inventory - Raw Materials',
  245: '1230 Inventory - Finished Goods (Purchased)',
  297: '1240 Inventory - Shipping Materials',
  322: '1250 Inventory - Supplies',
};
const assetLabel = (nsId) => ASSET_ACCOUNT_LABELS[nsId] || `NS account ${nsId}`;

function useDebouncedSearch(fetcher, minLen = 2) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    if (query.trim().length < minLen) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        setResults(await fetcher(query.trim()));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, fetcher, minLen]);
  return { query, setQuery, results, setResults, searching };
}

const searchItems = async (q) => {
  const { data } = await axios.get('/api/native-items', { params: { q, pageSize: 15, includeInactive: 'true' } });
  return data.items || [];
};

export default function NativeInventoryAdjustmentNew() {
  const [step, setStep] = useState('edit'); // edit | review | done
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountNsId, setAccountNsId] = useState(287);
  const [lines, setLines] = useState([]);
  const [zeroMode, setZeroMode] = useState(false); // true => server recomputes all lines at submit
  const [memo, setMemo] = useState('');
  const [loadingZero, setLoadingZero] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [writesEnabled, setWritesEnabled] = useState(null);
  const sourceKey = useRef(newRef());
  const amend = useAmendMode();
  const [amendOfNumber, setAmendOfNumber] = useState(null);

  const itemSearch = useDebouncedSearch(searchItems);

  useEffect(() => {
    axios.get('/api/native-inventory-adjustments/locations')
      .then(({ data }) => setLocations(data.locations || []))
      .catch(() => setLocations([]));
    axios.get('/api/native-inventory-adjustments/accounts')
      .then(({ data }) => setAccounts(data.accounts || []))
      .catch(() => setAccounts([]));
    axios.get('/api/native-inventory-adjustments', { params: { limit: 1 } })
      .then(({ data }) => setWritesEnabled(data.writesEnabled === true))
      .catch(() => setWritesEnabled(null));
  }, []);

  // Amend (?amendOf=<adjustment id>): prefill the location, account and every
  // line from the adjustment being corrected.
  //
  // A zero-out adjustment is prefilled as its EXPLICIT lines, not as zeroMode.
  // zeroOutLocation recomputes from on-hand at submit time, and at that moment
  // the reversal has just put the stock back — re-running it would write the
  // location off all over again with no correction applied.
  useEffect(() => {
    if (!amend.amendOf || locations.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`/api/native-inventory-adjustments/${encodeURIComponent(amend.amendOf)}`);
        if (cancelled || !data.adjustment) return;
        const a = data.adjustment;
        const loc = locations.find((l) => l.id === a.location_id);
        if (loc) setLocation(loc);
        if (a.adjustment_account_ns_id) setAccountNsId(Number(a.adjustment_account_ns_id));
        setMemo(a.memo || '');
        setAmendOfNumber(a.adjustment_number);
        setZeroMode(false);
        setLines((data.lines || []).map((l) => ({
          itemId: l.item_id,
          sku: l.sku,
          name: l.item_name || l.sku,
          onHand: l.on_hand_before,
          qtyDelta: String(Number(l.qty_delta)),
          unitCost: l.unit_cost,
        })));
      } catch { /* prefill is best-effort; the form stays blank */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const pickLocation = (l) => {
    setLocation(l);
    setLines([]);
    setZeroMode(false);
    setError(null);
  };

  const addItem = async (item) => {
    if (!location) return;
    if (lines.some((l) => l.itemId === item.id)) {
      itemSearch.setQuery('');
      itemSearch.setResults([]);
      return;
    }
    const placeholder = {
      itemId: item.id,
      sku: item.sku,
      name: item.name,
      onHand: null,
      qtyDelta: '',
      newQty: '',
      unitCost: null,
      costSource: null,
      assetAccountNsId: null,
      loading: true,
    };
    setLines((prev) => [...prev, placeholder]);
    setZeroMode(false);
    itemSearch.setQuery('');
    itemSearch.setResults([]);
    try {
      const { data } = await axios.get('/api/native-inventory-adjustments/line-info', {
        params: { locationId: location.id, itemId: item.id },
      });
      setLines((prev) => prev.map((l) => (l.itemId === item.id ? {
        ...l,
        loading: false,
        onHand: data.onHand,
        unitCost: data.unitCost,
        costSource: data.costSource,
        assetAccountNsId: data.assetAccountNsId,
      } : l)));
    } catch {
      setLines((prev) => prev.map((l) => (l.itemId === item.id ? { ...l, loading: false, onHand: 0, unitCost: 0, costSource: 'none' } : l)));
    }
  };

  const loadZeroOut = async () => {
    if (!location) return;
    setLoadingZero(true);
    setError(null);
    try {
      const { data } = await axios.get('/api/native-inventory-adjustments/zero-out', {
        params: { locationId: location.id },
      });
      setLines((data.lines || []).map((l) => ({
        itemId: l.itemId,
        sku: l.sku,
        name: l.itemName,
        onHand: l.onHandBefore,
        qtyDelta: String(l.qtyDelta),
        newQty: '0',
        unitCost: l.unitCost,
        costSource: l.costSource,
        assetAccountNsId: l.assetAccountNsId,
        loading: false,
      })));
      setZeroMode(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoadingZero(false);
    }
  };

  // Editing any line turns the document back into an explicit manual
  // adjustment (the server only recomputes when zeroOutLocation is sent).
  const updateLine = (itemId, patch) => {
    setZeroMode(false);
    setLines((prev) => prev.map((l) => {
      if (l.itemId !== itemId) return l;
      const next = { ...l, ...patch };
      if (patch.newQty !== undefined) {
        const nq = Number(patch.newQty);
        next.qtyDelta = patch.newQty === '' || !Number.isFinite(nq) || l.onHand == null ? '' : String(nq - Number(l.onHand));
      }
      if (patch.qtyDelta !== undefined) {
        const d = Number(patch.qtyDelta);
        next.newQty = patch.qtyDelta === '' || !Number.isFinite(d) || l.onHand == null ? '' : String(Number(l.onHand) + d);
      }
      return next;
    }));
  };
  const removeLine = (itemId) => { setZeroMode(false); setLines((prev) => prev.filter((l) => l.itemId !== itemId)); };

  const valuedLines = useMemo(() => lines.map((l) => ({
    ...l,
    valueDelta: Math.round(((Number(l.qtyDelta) || 0) * (Number(l.unitCost) || 0)) * 100) / 100,
  })), [lines]);

  const totals = useMemo(() => ({
    qtyDelta: valuedLines.reduce((s, l) => s + (Number(l.qtyDelta) || 0), 0),
    valueDelta: Math.round(valuedLines.reduce((s, l) => s + l.valueDelta, 0) * 100) / 100,
  }), [valuedLines]);

  // GL preview: asset lines grouped by account, one balancing adjustment line.
  const glPreview = useMemo(() => {
    const byAccount = new Map();
    let net = 0;
    for (const l of valuedLines) {
      if (!l.valueDelta) continue;
      net = Math.round((net + l.valueDelta) * 100) / 100;
      const key = l.assetAccountNsId || 244;
      const agg = byAccount.get(key) || { debit: 0, credit: 0 };
      if (l.valueDelta > 0) agg.debit = Math.round((agg.debit + l.valueDelta) * 100) / 100;
      else agg.credit = Math.round((agg.credit - l.valueDelta) * 100) / 100;
      byAccount.set(key, agg);
    }
    const rows = [...byAccount.entries()].map(([nsId, a]) => ({ label: assetLabel(nsId), ...a }));
    if (rows.length > 0 && net !== 0) {
      const adj = accounts.find((a) => a.nsId === Number(accountNsId));
      rows.push({
        label: adj ? `${adj.accountNumber} ${adj.name}` : `NS account ${accountNsId}`,
        debit: net < 0 ? -net : 0,
        credit: net > 0 ? net : 0,
        isAdjustment: true,
      });
    }
    return rows;
  }, [valuedLines, accounts, accountNsId]);

  const validate = () => {
    if (!location) return 'Pick a location first.';
    if (lines.length === 0) return 'Add at least one line, or use Zero out location.';
    for (const l of lines) {
      if (l.loading) return `Still loading ${l.sku}…`;
      const d = Number(l.qtyDelta);
      if (!Number.isFinite(d) || d === 0) return `${l.sku}: enter a non-zero quantity change (or remove the line).`;
    }
    return null;
  };

  const goReview = () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError(null);
    setStep('review');
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        sourceKey: sourceKey.current,
        location: { id: location.id },
        adjustmentAccountNsId: Number(accountNsId),
        memo: memo.trim() || null,
        ...(zeroMode
          ? { zeroOutLocation: true }
          : { lines: lines.map((l) => ({ itemId: l.itemId, sku: l.sku, qtyDelta: Number(l.qtyDelta) })) }),
        ...amend.bodyExtras(),
      };
      const { data } = await axios.post(amend.postUrl('/api/native-inventory-adjustments'), body);
      setConfirmation(data);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setStep('edit');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    sourceKey.current = newRef();
    setStep('edit');
    setLocation(null);
    setLines([]);
    setZeroMode(false);
    setMemo('');
    setAccountNsId(287);
    setConfirmation(null);
    setError(null);
  };

  const selectedAccount = accounts.find((a) => a.nsId === Number(accountNsId));

  // ── confirmation ───────────────────────────────────────────────────────────
  if (step === 'done' && confirmation) {
    const adj = confirmation.adjustment;
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2>Adjustment posted</h2>
          <div className="nia-confirm-grid">
            <span>Adjustment</span><code>{adj.adjustment_number}</code>
            <span>Lines</span><strong>{adj.line_count}</strong>
            <span>Quantity change</span><strong>{qty(adj.total_qty_delta)}</strong>
            <span>Value change</span><strong>{money(adj.total_value_delta)}</strong>
            <span>Native movement</span><strong>{adj.movement_status || '—'}</strong>
            <span>Journal entry</span><strong>{adj.gl_status || '—'}</strong>
            <span>NetSuite</span><strong>{adj.ns_status}{adj.ns_transaction_id ? ` (${adj.ns_transaction_id})` : ''}</strong>
          </div>
          {confirmation.netsuiteNote && <p className="nia-note">{confirmation.netsuiteNote}</p>}
          <button className="nia-btn nia-btn-primary" onClick={reset}>Create another adjustment</button>
        </div>
      </div>
    );
  }

  // ── review ─────────────────────────────────────────────────────────────────
  if (step === 'review') {
    return (
      <div className="nia-page">
        <div className="nia-header">
          <button className="nia-btn nia-btn-ghost" onClick={() => setStep('edit')}><ArrowLeft size={16} /> Back to edit</button>
          <h1>{amend.isAmend ? `Review amendment of ${amendOfNumber || 'adjustment'}` : 'Review adjustment'}</h1>
        </div>
        {error && <div className="nia-error">{error}</div>}
        {/* The reason lives on the review step because this is the submit
            point — the edit step can be open for a long time, and the banner
            is meant to be the last thing read before the reversal posts. */}
        <AmendBanner amend={amend} docLabel={amendOfNumber} what="the adjustment" />
        <div className="nia-card">
          <div className="nia-review-row"><span>Location</span><strong>{location?.name}</strong><code>NS {location?.nsInternalId}</code></div>
          <div className="nia-review-row">
            <span>Adjustment account</span>
            <strong>{selectedAccount ? `${selectedAccount.accountNumber} ${selectedAccount.name}` : `NS ${accountNsId}`}</strong>
          </div>
          {zeroMode && (
            <div className="nia-review-row">
              <span>Mode</span>
              <strong>Zero out location</strong>
              <em>quantities re-read from the live native on-hand at post time</em>
            </div>
          )}
          {memo && <div className="nia-review-row"><span>Memo</span><em>{memo}</em></div>}
          <table className="nia-table">
            <thead><tr><th>SKU</th><th>Item</th><th className="r">On hand</th><th className="r">Change</th><th className="r">Unit cost</th><th className="r">Value</th></tr></thead>
            <tbody>
              {valuedLines.map((l) => (
                <tr key={l.itemId}>
                  <td><code>{l.sku}</code></td>
                  <td>{l.name}</td>
                  <td className="r">{l.onHand == null ? '—' : qty(l.onHand)}</td>
                  <td className="r">{qty(l.qtyDelta)}</td>
                  <td className="r">{l.unitCost == null ? '—' : money(l.unitCost)}</td>
                  <td className="r">{money(l.valueDelta)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="nia-totals">
            <div><span>Total quantity change</span><strong>{qty(totals.qtyDelta)}</strong></div>
            <div className="nia-total-line"><span>Total value change</span><strong>{money(totals.valueDelta)}</strong></div>
          </div>

          <h3 className="nia-gl-heading">Journal entry preview</h3>
          <table className="nia-table">
            <thead><tr><th>Account</th><th className="r">Debit</th><th className="r">Credit</th></tr></thead>
            <tbody>
              {glPreview.map((r, i) => (
                <tr key={i} className={r.isAdjustment ? 'nia-gl-adj' : ''}>
                  <td>{r.label}</td>
                  <td className="r">{r.debit ? money(r.debit) : ''}</td>
                  <td className="r">{r.credit ? money(r.credit) : ''}</td>
                </tr>
              ))}
              {glPreview.length === 0 && (
                <tr><td colSpan={3}><em>No GL impact — every line has zero cost basis.</em></td></tr>
              )}
            </tbody>
          </table>
          <p className="nia-hint">
            Posts a native inventory movement + this journal entry immediately, then creates the NetSuite
            inventory adjustment (best effort — a NetSuite failure leaves the native document posted and retryable).
            Amounts are estimates from average cost; once NetSuite posts, the mirror trues the journal up to
            NetSuite&apos;s exact costing.
          </p>
          <div className="nia-actions">
            <button className="nia-btn nia-btn-ghost" onClick={() => setStep('edit')}>Back</button>
            <button
              className="nia-btn nia-btn-primary"
              disabled={submitting || (amend.isAmend && !amend.amendReasonValid)}
              onClick={submit}
            >
              {submitting ? 'Posting…' : amend.isAmend ? 'Reverse and repost' : 'Post adjustment'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── edit ───────────────────────────────────────────────────────────────────
  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><Scale size={22} /> {amend.isAmend ? `Amend ${amendOfNumber || 'Inventory Adjustment'}` : 'Inventory Adjustment (Native)'}</h1>
        <p className="nia-sub">
          {amend.isAmend
            ? 'Prefilled from the adjustment being corrected. Posting reverses it in full and reposts under the same number — you give the reason on the review step.'
            : 'Adjust native on-hand with real accounting: quantity movement + write-off journal + NetSuite copy.'}
        </p>
      </div>
      {writesEnabled === false && (
        <div className="nia-banner">
          Posting is currently disabled (NATIVE_INVENTORY_ADJUSTMENTS_ENABLED is off on the Run server).
          You can build and preview an adjustment, but the post will be rejected.
        </div>
      )}
      {error && <div className="nia-error">{error}</div>}

      <div className="nia-card">
        <h3>Location</h3>
        {location ? (
          <div className="nia-chip">
            <div>
              <strong>{location.name}</strong>
              <code>NS {location.nsInternalId}</code>
              {!location.isActive && <span className="nia-tag nia-tag-warn">closed in NS</span>}
              <em>{location.nativeItemCount} items, {qty(location.nativeUnits)} units native on-hand</em>
            </div>
            <button className="nia-icon-btn" onClick={() => pickLocation(null)} title="Change location"><X size={16} /></button>
          </div>
        ) : (
          <div className="nia-locations">
            {locations.filter((l) => l.nativeItemCount > 0 || l.isActive).map((l) => (
              <button key={l.id} className="nia-location" onClick={() => pickLocation(l)}>
                <strong>{l.name}</strong>
                <span>
                  {l.nativeItemCount > 0 ? `${l.nativeItemCount} items, ${qty(l.nativeUnits)} units` : 'no native on-hand'}
                  {!l.isActive && ' — closed in NS'}
                </span>
              </button>
            ))}
            {locations.length === 0 && <div className="nia-result-muted">Loading locations…</div>}
          </div>
        )}
      </div>

      {location && (
        <div className="nia-card">
          <h3>Adjustment account</h3>
          <select className="nia-input nia-select" value={accountNsId} onChange={(e) => setAccountNsId(Number(e.target.value))}>
            {!accounts.some((a) => a.nsId === 287) && <option value={287}>5180 Inventory Adjustments</option>}
            {accounts.map((a) => (
              <option key={a.nsId} value={a.nsId}>{a.accountNumber} {a.name}</option>
            ))}
          </select>
          <p className="nia-hint">The write-off side of the journal. Default 5180 Inventory Adjustments (NS 287) — scrap, QC damage, liquidation etc. are in the list.</p>
        </div>
      )}

      {location && (
        <div className="nia-card">
          <div className="nia-lines-header">
            <h3>Lines</h3>
            <button className="nia-btn nia-btn-mini" onClick={loadZeroOut} disabled={loadingZero || location.nativeItemCount === 0}>
              <Eraser size={14} /> {loadingZero ? 'Loading…' : `Zero out location (${location.nativeItemCount} items)`}
            </button>
          </div>
          {zeroMode && (
            <div className="nia-zero-note">
              Zero-out mode: every native on-hand line at {location.name} is set to 0. Quantities are re-read from the
              live ledger when you post, so a movement between now and then cannot leave a remainder. Editing any line
              switches to a manual adjustment.
            </div>
          )}
          <div className="nia-search">
            <Search size={16} className="nia-search-icon" />
            <input
              className="nia-input nia-input-search"
              placeholder="Add item — search by SKU or name…"
              value={itemSearch.query}
              onChange={(e) => itemSearch.setQuery(e.target.value)}
            />
            {(itemSearch.results.length > 0 || itemSearch.searching) && (
              <div className="nia-results">
                {itemSearch.searching && <div className="nia-result-muted">Searching…</div>}
                {itemSearch.results.map((it) => (
                  <button key={it.id} className="nia-result" onClick={() => addItem(it)}>
                    <code>{it.sku}</code>
                    <strong>{it.name}</strong>
                    {it.isActive === false && <span className="nia-tag">inactive</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {lines.length > 0 && (
            <table className="nia-table">
              <thead>
                <tr>
                  <th>SKU</th><th>Item</th>
                  <th className="r">On hand</th>
                  <th className="r">New qty</th>
                  <th className="r">Change</th>
                  <th className="r">Unit cost</th>
                  <th className="r">Value</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {valuedLines.map((l) => (
                  <tr key={l.itemId}>
                    <td><code>{l.sku}</code></td>
                    <td>
                      {l.name}
                      {l.costSource && l.costSource !== 'none' && <div className="nia-cost-source">cost: {l.costSource.replace(/_/g, ' ')}</div>}
                      {l.costSource === 'none' && <div className="nia-cost-source nia-cost-none">no cost basis — posts quantity only</div>}
                    </td>
                    <td className="r">{l.loading ? '…' : l.onHand == null ? '—' : qty(l.onHand)}</td>
                    <td className="r">
                      <input type="number" step="1" className="nia-input nia-input-num" value={l.newQty}
                        onChange={(e) => updateLine(l.itemId, { newQty: e.target.value })} />
                    </td>
                    <td className="r">
                      <input type="number" step="1" className="nia-input nia-input-num" value={l.qtyDelta}
                        onChange={(e) => updateLine(l.itemId, { qtyDelta: e.target.value })} />
                    </td>
                    <td className="r">{l.unitCost == null ? '…' : money(l.unitCost)}</td>
                    <td className="r">{money(l.valueDelta)}</td>
                    <td><button className="nia-icon-btn" onClick={() => removeLine(l.itemId)} title="Remove line"><Trash2 size={15} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {location && lines.length > 0 && (
        <div className="nia-card">
          <h3>Journal entry preview</h3>
          <table className="nia-table">
            <thead><tr><th>Account</th><th className="r">Debit</th><th className="r">Credit</th></tr></thead>
            <tbody>
              {glPreview.map((r, i) => (
                <tr key={i} className={r.isAdjustment ? 'nia-gl-adj' : ''}>
                  <td>{r.label}</td>
                  <td className="r">{r.debit ? money(r.debit) : ''}</td>
                  <td className="r">{r.credit ? money(r.credit) : ''}</td>
                </tr>
              ))}
              {glPreview.length === 0 && (
                <tr><td colSpan={3}><em>No GL impact — lines have zero cost basis.</em></td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {location && (
        <div className="nia-card">
          <h3>Memo</h3>
          <textarea className="nia-input" rows={2} placeholder="Why this adjustment exists — appears on the journal and in NetSuite"
            value={memo} onChange={(e) => setMemo(e.target.value)} />
        </div>
      )}

      <div className="nia-footer">
        <div className="nia-footer-totals">
          <span>{lines.length} line{lines.length === 1 ? '' : 's'}</span>
          <span>Qty {qty(totals.qtyDelta)}</span>
          <strong>Value {money(totals.valueDelta)}</strong>
        </div>
        <button className="nia-btn nia-btn-primary" onClick={goReview} disabled={!location || lines.length === 0}>
          Review adjustment
        </button>
      </div>
    </div>
  );
}
